import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { api } from '../../lib/api';
import { History, Loader2, ArrowRight, UserCheck, UserMinus } from 'lucide-react';
import { formatDate } from '../../lib/utils';

const actionBadge = (a) => {
  if (a === 'assigned') return { label: 'Assignation', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
  if (a === 'reassigned') return { label: 'Réassignation', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { label: 'Désassignation', cls: 'bg-gray-50 text-gray-600 border-gray-200' };
};

const AssignmentHistoryPage = () => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/candidates/__assignment-history?limit=200');
        if (res.ok) {
          const d = await res.json();
          setHistory(d.history || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group per candidate to give a readable timeline
  const grouped = history.reduce((acc, h) => {
    acc[h.candidate_id] = acc[h.candidate_id] || { name: h.candidate_name, items: [] };
    acc[h.candidate_id].items.push(h);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6" data-testid="assignment-history-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-tp-text flex items-center gap-2">
          <History className="h-6 w-6 md:h-7 md:w-7 text-tp-accent" />
          Historique des assignations
        </h1>
        <p className="text-sm text-tp-text-light mt-1">
          Trace complète : qui a assigné quel candidat à qui et quand. 200 dernières entrées, ordre chronologique inverse.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-tp-accent" /></div>
      ) : history.length === 0 ? (
        <Card className="p-10 text-center border-tp-border" data-testid="assignment-history-empty">
          <History className="h-10 w-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-tp-text-light">
            Aucune assignation pour l'instant. Dès que vous assignerez un candidat, l'événement apparaîtra ici.
          </p>
        </Card>
      ) : (
        <Card className="border-tp-border">
          <div className="p-4 md:p-5 border-b border-tp-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-tp-text">{history.length} événement{history.length > 1 ? 's' : ''}</h2>
            <span className="text-xs text-tp-text-light">{Object.keys(grouped).length} candidat{Object.keys(grouped).length > 1 ? 's' : ''} impacté{Object.keys(grouped).length > 1 ? 's' : ''}</span>
          </div>
          <ul className="divide-y divide-tp-border" data-testid="assignment-history-list">
            {history.map((h) => {
              const b = actionBadge(h.action);
              const Icon = h.action === 'unassigned' ? UserMinus : UserCheck;
              return (
                <li
                  key={h._id || h.id}
                  className="p-4 md:p-5 flex items-start gap-3 hover:bg-tp-bg/30 transition"
                  data-testid={`assignment-history-item`}
                >
                  <div className="w-9 h-9 rounded-lg bg-tp-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-tp-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${b.cls}`}>{b.label}</span>
                      <Link
                        to={`/admin/candidates/${h.candidate_id}`}
                        className="text-sm font-medium text-tp-text hover:text-tp-accent truncate"
                      >
                        {h.candidate_name || 'Candidat sans nom'}
                      </Link>
                    </div>
                    <div className="text-xs text-tp-text-light flex items-center gap-1.5 flex-wrap">
                      {h.action === 'unassigned' && (
                        <>
                          <span className="font-medium text-tp-text">{h.previous_assignee_name || h.previous_assignee}</span>
                          <span>retiré</span>
                        </>
                      )}
                      {h.action === 'assigned' && (
                        <>
                          <span>Assigné à</span>
                          <span className="font-medium text-tp-text">{h.new_assignee_name || h.new_assignee}</span>
                        </>
                      )}
                      {h.action === 'reassigned' && (
                        <>
                          <span className="font-medium text-tp-text">{h.previous_assignee_name || h.previous_assignee}</span>
                          <ArrowRight className="h-3 w-3" />
                          <span className="font-medium text-tp-text">{h.new_assignee_name || h.new_assignee}</span>
                        </>
                      )}
                    </div>
                    <p className="text-[11px] text-tp-text-light mt-1">
                      par {h.changed_by_name || h.changed_by} · {formatDate(h.created_at)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default AssignmentHistoryPage;
